import { useCallback, useEffect, useState } from 'react';

import { me as fetchMe, type Me } from './api.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { Home } from './pages/Home.js';
import { Login } from './pages/Login.js';
import { Operator } from './pages/Operator.js';
import { ResetPassword } from './pages/ResetPassword.js';
import { inviteTokenFrom, navigate, resetTokenFrom, usePath } from './router.js';

type State =
  | { readonly status: 'checking' }
  | { readonly status: 'signed_out' }
  | { readonly status: 'signed_in'; readonly me: Me };

/**
 * Is this the same caller, seeing the same things, as a moment ago?
 *
 * Only the fields that change what is on screen. `can` is derived from the role
 * and the customisation, so it does not need comparing separately — and the
 * support visit is here because a visit STARTING is exactly the kind of thing
 * an open tab must not sleep through.
 */
function sameCaller(a: Me, b: Me): boolean {
  return (
    a.user_id === b.user_id &&
    a.company_id === b.company_id &&
    a.company_role === b.company_role &&
    a.finance_view === b.finance_view &&
    (a.support?.started_at ?? null) === (b.support?.started_at ?? null)
  );
}

export function App(): JSX.Element {
  const [state, setState] = useState<State>({ status: 'checking' });
  const path = usePath();
  const inviteToken = inviteTokenFrom(path);
  const resetToken = resetTokenFrom(path);

  /**
   * Ask the server who we are.
   *
   * The session lives in an HttpOnly cookie, which JavaScript deliberately
   * cannot read — so "am I signed in?" is a question only the server can
   * answer. That is the point: a token this code could read is a token an
   * injected script could steal.
   */
  const refresh = useCallback(async () => {
    try {
      setState({ status: 'signed_in', me: await fetchMe() });
    } catch {
      setState({ status: 'signed_out' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Ask AGAIN whenever the tab comes back to the front.
   *
   * The session is a cookie, and a cookie belongs to the browser rather than to
   * this tab. Signing in as somebody else next door replaces it underneath —
   * accepting an invitation does exactly that, and so does a second account on
   * a shared site tablet — and until now this tab carried on rendering the
   * person it had booted as. That is worse than being refused: the owner's
   * corner read "Company Admin" while every request was being answered as a
   * site engineer, so People was empty and Projects said "No projects yet" and
   * nothing on the screen explained why. A refusal you can read is fine; an
   * identity that quietly stops being true is not.
   *
   * Only a CHANGE is written to state. Somebody alt-tabbing back to a screen
   * they never left should not have the whole app redrawn under them.
   */
  useEffect(() => {
    function recheck(): void {
      if (document.visibilityState !== 'visible') return;
      void (async () => {
        try {
          const fresh = await fetchMe();
          setState((current) =>
            current.status === 'signed_in' && sameCaller(current.me, fresh)
              ? current
              : { status: 'signed_in', me: fresh },
          );
        } catch {
          // Signed out somewhere else, or the session expired while the tab sat
          // in the background. Either way this is the sign-in screen now.
          setState((current) =>
            current.status === 'signed_out' ? current : { status: 'signed_out' },
          );
        }
      })();
    }
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, []);

  /*
   * THE OPERATOR CONSOLE IS CHECKED FIRST, AND IS NOT PART OF THIS APP.
   *
   * It runs on its own cookie and its own identity — the vendor, who is in no
   * company (see `apps/api/src/operator/service.ts`). Checked before the
   * customer session for two reasons: an operator is usually NOT signed in as a
   * customer, so the check below would bounce them to the sign-in screen; and
   * somebody who IS signed in as a customer must not have `/operator` quietly
   * resolve to their own home page.
   */
  if (path === '/operator' || path.startsWith('/operator/')) {
    const setup = /^\/operator\/setup\/(.+)$/.exec(path);
    return <Operator setupToken={setup ? decodeURIComponent(setup[1]!) : null} />;
  }

  // Checked BEFORE the session, and regardless of it. Someone following an
  // invitation has no account yet, and someone already signed in as a different
  // person must not be silently shown their own home page instead.
  if (inviteToken) {
    return (
      <AcceptInvite
        token={inviteToken}
        signedInAs={state.status === 'signed_in' ? state.me : null}
        onJoined={() => {
          navigate('/projects');
          void refresh();
        }}
      />
    );
  }

  // Same rule as the invitation above, and it matters more here: somebody
  // resetting a password is often on a device already signed in as somebody
  // else — a shared site tablet — and must not be bounced to that person's
  // home page instead of the link they were given.
  if (resetToken) {
    return (
      <ResetPassword
        token={resetToken}
        signedInAs={state.status === 'signed_in' ? state.me : null}
        onReset={() => {
          navigate('/projects');
          void refresh();
        }}
      />
    );
  }

  if (state.status === 'checking') {
    return (
      <main className="auth-shell">
        <p aria-live="polite">Loading…</p>
      </main>
    );
  }

  if (state.status === 'signed_out') {
    return <Login onSignedIn={refresh} />;
  }

  return <Home me={state.me} onSignedOut={() => setState({ status: 'signed_out' })} />;
}
